import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { patientApi } from '../api';
import Spinner from '../../../components/ui/Spinner';
import OfficialDocumentLayout from '../components/documents/OfficialDocumentLayout';
import {
  CertificateTemplate, VaccinationCertificateTemplate, PrescriptionTemplate,
  LabReportTemplate, DischargeSummaryTemplate, AmbulanceCertificateTemplate,
  ECCEmergencyCertificateTemplate, BloodDonationCertificateTemplate,
  InsuranceClaimPackTemplate, InvoiceReceiptTemplate,
  AppointmentReceiptTemplate, PharmacyDocumentTemplate, HospitalDocumentTemplate,
  AmbulanceInvoiceTemplate, BloodBankDocumentTemplate, InsuranceDocumentTemplate,
  LabDocumentTemplate, PaymentReceiptTemplate, TEMPLATE_MAP,
} from '../templates';

const CERT_TYPE_LABELS: Record<string, string> = {
  VACCINATION_CERTIFICATE: 'Vaccination Certificate',
  MEDICAL_FITNESS_CERTIFICATE: 'Certificate of Fitness',
  SICK_LEAVE_CERTIFICATE: 'Sick Leave Certificate',
  LAB_REPORT_CERTIFICATE: 'Lab Report Verification',
  PRESCRIPTION_CERTIFICATE: 'Prescription Certificate',
  DISCHARGE_SUMMARY_CERTIFICATE: 'Discharge Summary',
  HOSPITAL_ADMISSION_CERTIFICATE: 'Hospital Admission Certificate',
  SURGERY_PROCEDURE_CERTIFICATE: 'Surgery / Procedure Certificate',
  INSURANCE_CLAIM_CERTIFICATE: 'Insurance Claim Pack',
  AMBULANCE_TRANSPORT_CERTIFICATE: 'Ambulance Transport Certificate',
  EMERGENCY_EVENT_CERTIFICATE: 'Emergency Event Certificate',
  BLOOD_DONATION_CERTIFICATE: 'Blood Donation Certificate',
  HEALTH_CHECKUP_CERTIFICATE: 'Health Checkup Certificate',
  INSTITUTION_CLEARANCE_CERTIFICATE: 'Institution Medical Clearance',
  BIRTH_CERTIFICATE: 'Birth Certificate',
  DEATH_CERTIFICATE: 'Death Certificate',
  CHRONIC_CARE_CERTIFICATE: 'Chronic Care Certificate',
  VACCINATION_COMPLETION_CERTIFICATE: 'Vaccination Completion Certificate',
};

function getWatermark(status: string): 'DEMO' | 'REVOKED' | 'EXPIRED' | 'DRAFT' | null {
  if (status === 'REVOKED') return 'REVOKED';
  if (status === 'EXPIRED') return 'EXPIRED';
  if (status === 'DRAFT') return 'DRAFT';
  return null;
}

// Maps the certificate's stored metadata onto the shape each template expects.
// Only real issuer-provided metadata is rendered — a field the issuer did not
// record stays blank on the document. Money fields default to 0 because the
// templates format them unconditionally.
function buildTemplateData(certType: string, metadata: any) {
  const m = metadata || {};
  const signatories = m.signatories
    || (m.issuerName
      ? [{ name: m.issuerName, role: m.issuerRole || 'Authorized Signatory', institution: m.institutionName, nhcId: m.issuerNhcId }]
      : []);

  switch (certType) {
    case 'VACCINATION_CERTIFICATE':
    case 'VACCINATION_COMPLETION_CERTIFICATE':
      return {
        vaccines: m.vaccines || [],
        postInstructions: m.postInstructions || [],
        signatories,
      };
    case 'PRESCRIPTION_CERTIFICATE':
      return {
        diagnosis: m.diagnosis,
        complaints: m.complaints,
        medicines: m.medicines || [],
        advice: m.advice || [],
        followUpDate: m.followUpDate,
        doctorName: m.doctorName,
        doctorNhcId: m.doctorNhcId,
        doctorLicense: m.doctorLicense,
        speciality: m.speciality,
        signatories,
      };
    case 'AMBULANCE_TRANSPORT_CERTIFICATE':
      return {
        dispatchId: m.dispatchId,
        callType: m.callType,
        priority: m.priority,
        pickupLocation: m.pickupLocation,
        dropLocation: m.dropLocation,
        dispatchTime: m.dispatchTime,
        arrivalTime: m.arrivalTime,
        totalTime: m.totalTime,
        distance: m.distance,
        crewParamedic: m.crewParamedic,
        ambulanceNo: m.ambulanceNo,
        patientCondition: m.patientCondition || [],
        equipment: m.equipment || [],
        handoverNotes: m.handoverNotes,
        signatories,
      };
    case 'BLOOD_DONATION_CERTIFICATE':
      return {
        donationId: m.donationId,
        donationType: m.donationType,
        bloodGroup: m.bloodGroup,
        units: m.units,
        donationDate: m.donationDate,
        venue: m.venue,
        campName: m.campName,
        hemoglobin: m.hemoglobin,
        bpReading: m.bpReading,
        bloodBankName: m.bloodBankName,
        bloodBankLicense: m.bloodBankLicense,
        componentsExtracted: m.componentsExtracted || [],
        nextEligibleDate: m.nextEligibleDate,
        signatories,
      };
    case 'INSURANCE_CLAIM_CERTIFICATE':
      return {
        claimId: m.claimId,
        policyNumber: m.policyNumber,
        insurerName: m.insurerName,
        planName: m.planName,
        claimType: m.claimType,
        claimAmount: m.claimAmount ?? 0,
        approvedAmount: m.approvedAmount ?? 0,
        claimStatus: m.claimStatus,
        admissionDate: m.admissionDate,
        dischargeDate: m.dischargeDate,
        diagnosis: m.diagnosis || [],
        procedures: m.procedures || [],
        documents: m.documents || [],
        remarks: m.remarks,
        signatories,
      };
    case 'DISCHARGE_SUMMARY_CERTIFICATE':
      return {
        admissionDate: m.admissionDate,
        dischargeDate: m.dischargeDate,
        wardBed: m.wardBed,
        admittingDoctor: m.admittingDoctor,
        diagnosis: m.diagnosis || [],
        procedures: m.procedures || [],
        hospitalCourse: m.hospitalCourse,
        conditionAtDischarge: m.conditionAtDischarge,
        medications: m.medications || [],
        followUpInstructions: m.followUpInstructions || [],
        emergencyInstructions: m.emergencyInstructions,
        signatories,
      };
    case 'EMERGENCY_EVENT_CERTIFICATE':
      return {
        incidentId: m.incidentId,
        emergencyType: m.emergencyType,
        severity: m.severity,
        locationAddress: m.locationAddress,
        dispatchTime: m.dispatchTime,
        responseTime: m.responseTime,
        hospitalName: m.hospitalName,
        triageCategory: m.triageCategory,
        initialAssessment: m.initialAssessment,
        interventions: m.interventions || [],
        outcomeStatus: m.outcomeStatus,
        handoverDoctor: m.handoverDoctor,
        handoverTime: m.handoverTime,
        signatories,
      };
    default: {
      const mapped = TEMPLATE_MAP[certType];
      if (mapped === 'AppointmentReceiptTemplate') {
        return {
          appointmentId: m.appointmentId,
          appointmentDate: m.appointmentDate,
          appointmentTime: m.appointmentTime,
          consultationMode: m.consultationMode,
          doctorName: m.doctorName,
          doctorSpecialty: m.doctorSpecialty,
          doctorNhcId: m.doctorNhcId,
          clinicName: m.clinicName,
          clinicAddress: m.clinicAddress,
          status: m.status,
          fee: m.fee ?? 0,
          paymentMode: m.paymentMode,
          paymentStatus: m.paymentStatus,
          transactionId: m.transactionId,
          diagnosis: m.diagnosis,
          notes: m.notes,
          followUpDate: m.followUpDate,
          signatories,
        };
      }
      if (mapped === 'PharmacyDocumentTemplate') {
        const subtypeMap: Record<string, string> = { PHARMACY_ORDER_INVOICE: 'ORDER_INVOICE', PHARMACY_PAYMENT_RECEIPT: 'PAYMENT_RECEIPT', PRESCRIPTION_FULFILLMENT: 'FULFILLMENT_RECEIPT', MEDICINE_DELIVERY_RECEIPT: 'DELIVERY_RECEIPT', MEDICINE_RETURN_REFUND: 'RETURN_REFUND', MEDICINE_BATCH_EXPIRY: 'BATCH_EXPIRY_SHEET' };
        return {
          documentSubtype: subtypeMap[certType] || 'ORDER_INVOICE',
          orderId: m.orderId,
          orderDate: m.orderDate,
          pharmacyName: m.pharmacyName,
          medicines: m.medicines || [],
          subtotal: m.subtotal ?? 0, discount: m.discount ?? 0, gst: m.gst ?? 0, total: m.total ?? 0,
          amountPaid: m.amountPaid ?? 0, paymentMode: m.paymentMode, paymentStatus: m.paymentStatus,
          signatories,
        };
      }
      if (mapped === 'HospitalDocumentTemplate') {
        const subtypeMap: Record<string, string> = { OPD_REGISTRATION: 'OPD_REGISTRATION', OPD_INVOICE: 'OPD_INVOICE', IPD_ADMISSION: 'IPD_ADMISSION', IPD_DAILY_BILL: 'IPD_DAILY_BILL', BED_WARD_INVOICE: 'BED_WARD_INVOICE', NURSING_INVOICE: 'NURSING_INVOICE', DOCTOR_VISIT_INVOICE: 'DOCTOR_VISIT_INVOICE', OT_BOOKING: 'OT_BOOKING', OT_PROCEDURE_INVOICE: 'OT_PROCEDURE_INVOICE', SURGERY_CERTIFICATE: 'SURGERY_CERTIFICATE', ER_REGISTRATION: 'ER_REGISTRATION', ER_INVOICE: 'ER_INVOICE', HOSPITAL_DISCHARGE_SUMMARY: 'DISCHARGE_SUMMARY', FINAL_HOSPITAL_BILL: 'FINAL_BILL', DEPOSIT_RECEIPT: 'DEPOSIT_RECEIPT', HOSPITAL_REFUND_RECEIPT: 'REFUND_RECEIPT', LAMA_DAMA_TRANSFER: 'LAMA_DAMA_TRANSFER' };
        return {
          documentSubtype: subtypeMap[certType] || 'OPD_INVOICE',
          doctorName: m.doctorName, doctorNhcId: m.doctorNhcId,
          admissionDate: m.admissionDate,
          dischargeDate: m.dischargeDate,
          dischargeType: m.dischargeType,
          diagnosis: m.diagnosis,
          procedureName: m.procedureName,
          procedureDate: m.procedureDate,
          wardName: m.wardName,
          bedId: m.bedId,
          admissionId: m.admissionId,
          services: m.services || [],
          subtotal: m.subtotal ?? 0, discount: m.discount ?? 0, gst: m.gst ?? 0,
          patientPayable: m.patientPayable ?? 0, amountPaid: m.amountPaid ?? 0,
          paymentMode: m.paymentMode, paymentStatus: m.paymentStatus,
          notes: m.notes,
          signatories,
        };
      }
      if (mapped === 'AmbulanceInvoiceTemplate') {
        const subtypeMap: Record<string, string> = { AMBULANCE_BOOKING_RECEIPT: 'BOOKING_RECEIPT', AMBULANCE_TRIP_INVOICE: 'TRIP_INVOICE', AMBULANCE_TRANSPORT_CERTIFICATE: 'TRANSPORT_CERTIFICATE', ECC_EVENT_CERTIFICATE: 'ECC_EVENT_CERTIFICATE', ER_HANDOVER: 'ER_HANDOVER', PARAMEDIC_NOTE: 'PARAMEDIC_NOTE', AMBULANCE_CANCELLATION: 'CANCELLATION_RECEIPT' };
        return {
          documentSubtype: subtypeMap[certType] || 'TRIP_INVOICE',
          bookingId: m.bookingId, bookingDate: m.bookingDate,
          providerName: m.providerName, vehicleType: m.vehicleType,
          pickupLocation: m.pickupLocation, dropLocation: m.dropLocation,
          pickupTime: m.pickupTime, arrivalTime: m.arrivalTime, dropTime: m.dropTime,
          tripDistance: m.tripDistance,
          baseFare: m.baseFare ?? 0, perKmFare: m.perKmFare ?? 0, surcharge: m.surcharge ?? 0,
          totalFare: m.totalFare ?? 0, amountPaid: m.amountPaid ?? 0, paymentStatus: m.paymentStatus,
          signatories,
        };
      }
      if (mapped === 'BloodBankDocumentTemplate') {
        const subtypeMap: Record<string, string> = { BLOOD_DONATION_CERT: 'DONATION_CERTIFICATE', BLOOD_REQUEST_RECEIPT: 'REQUEST_RECEIPT', BLOOD_ISSUE_RECEIPT: 'ISSUE_RECEIPT', BLOOD_TRACEABILITY: 'TRACEABILITY_SHEET', CROSSMATCH_REPORT: 'CROSSMATCH_REPORT', BLOOD_BANK_INVOICE: 'BLOOD_BANK_INVOICE' };
        return {
          documentSubtype: subtypeMap[certType] || 'DONATION_CERTIFICATE',
          bloodBankName: m.bloodBankName, bloodGroup: m.bloodGroup,
          donorName: m.donorName, donationDate: m.donationDate,
          componentType: m.componentType, volume: m.volume,
          screeningStatus: m.screeningStatus,
          processingFee: m.processingFee ?? 0, totalAmount: m.totalAmount ?? 0,
          amountPaid: m.amountPaid ?? 0, paymentStatus: m.paymentStatus,
          signatories,
        };
      }
      if (mapped === 'InsuranceDocumentTemplate') {
        const subtypeMap: Record<string, string> = { INSURANCE_PRE_AUTH: 'PRE_AUTH_REQUEST', INSURANCE_CLAIM_PACK: 'CLAIM_PACK', INSURANCE_CLAIM_RECEIPT: 'CLAIM_RECEIPT', CLAIM_APPROVAL_LETTER: 'APPROVAL_LETTER', PATIENT_PAYABLE_ESTIMATE: 'PATIENT_ESTIMATE', INSURER_PAYABLE_ESTIMATE: 'INSURER_ESTIMATE', HOSPITAL_ESTIMATE_INSURANCE: 'HOSPITAL_ESTIMATE' };
        return {
          documentSubtype: subtypeMap[certType] || 'CLAIM_PACK',
          policyId: m.policyId, insurerName: m.insurerName,
          hospitalName: m.hospitalName,
          diagnosis: m.diagnosis,
          estimateAmount: m.estimateAmount ?? 0, approvedAmount: m.approvedAmount ?? 0,
          patientPayable: m.patientPayable ?? 0, insurerPayable: m.insurerPayable ?? 0,
          approvalStatus: m.approvalStatus,
          signatories,
        };
      }
      if (mapped === 'LabDocumentTemplate') {
        const subtypeMap: Record<string, string> = { LAB_BOOKING_RECEIPT: 'BOOKING_RECEIPT', LAB_SAMPLE_COLLECTION: 'SAMPLE_COLLECTION', LAB_TEST_INVOICE: 'TEST_INVOICE', HOME_SAMPLE_COLLECTION: 'HOME_COLLECTION', CRITICAL_LAB_ALERT: 'CRITICAL_ALERT', LAB_REPORT_VERIFICATION: 'VERIFICATION_CERTIFICATE' };
        return {
          documentSubtype: subtypeMap[certType] || 'TEST_INVOICE',
          labName: m.labName, labNhcId: m.labNhcId,
          sampleId: m.sampleId,
          tests: m.tests || [],
          collectionDate: m.collectionDate,
          subtotal: m.subtotal ?? 0, gst: m.gst ?? 0, total: m.total ?? 0,
          amountPaid: m.amountPaid ?? 0, paymentStatus: m.paymentStatus,
          signatories,
        };
      }
      if (mapped === 'PaymentReceiptTemplate') {
        const subtypeMap: Record<string, string> = { UNIFIED_PAYMENT_RECEIPT: 'UNIFIED_PAYMENT', WALLET_TRANSACTION_RECEIPT: 'WALLET_TRANSACTION', REFUND_ACKNOWLEDGEMENT: 'REFUND_ACKNOWLEDGEMENT', GST_TAX_INVOICE: 'GST_INVOICE', NHC_SERVICE_FEE: 'SERVICE_FEE' };
        return {
          documentSubtype: subtypeMap[certType] || 'UNIFIED_PAYMENT',
          receiptId: m.receiptId, receiptDate: m.receiptDate,
          payerName: m.payerName, payerNhcId: m.payerNhcId,
          payeeName: m.payeeName, payeeNhcId: m.payeeNhcId,
          paymentMode: m.paymentMode, description: m.description,
          amount: m.amount ?? 0, gstAmount: m.gstAmount, totalAmount: m.totalAmount ?? 0,
          status: m.status,
          signatories,
        };
      }
      return {
        certificateType: certType,
        body: m.body || '',
        clinicalDetails: m.clinicalDetails || [],
        conditions: m.conditions,
        recommendations: m.recommendations,
        validityPeriod: m.validityPeriod,
        signatories,
      };
    }
  }
}

// Linked identifiers shown in the document footer. Only identifiers that were
// actually recorded on the certificate or its metadata are listed — nothing is
// synthesized client-side.
function buildLinkedIds(certType: string, cert: any, metadata: any): { label: string; value: string }[] {
  const m = metadata || {};
  const ids: { label: string; value: string }[] = [];
  const push = (label: string, value: any) => { if (value) ids.push({ label, value: String(value) }); };

  push('Patient NHC ID', cert.nhcId);
  push('Document ID', cert.certificateId || cert.id);
  push('NeoVerify ID', cert.neoVerifyId);

  push('Doctor NHC ID', m.doctorNhcId || m.issuerNhcId);
  push('Doctor License', m.doctorLicense);
  push('Appointment ID', m.appointmentId);
  push('Clinic/Hospital ID', m.clinicId || m.institutionId);
  push('Prescription ID', m.prescriptionId);

  push('Hospital NHC ID', m.hospitalNhcId);
  push('Nurse NHC ID', m.nurseNhcId);
  push('Admission ID', m.admissionId);
  push('Ward/Bed', m.wardBedId);
  push('Discharge ID', m.dischargeId);
  push('Procedure ID', m.procedureId);

  push('Lab NHC ID', m.labNhcId);
  push('Pathologist ID', m.pathologistId);
  push('Sample ID', m.sampleId);
  push('Lab Report ID', m.labReportId);

  push('Ambulance Provider ID', m.ambulanceProviderId);
  push('Vehicle ID', m.ambulanceNo || m.vehicleId);
  push('Driver ID', m.driverId);
  push('Paramedic', m.crewParamedic);
  push('Dispatch ID', m.dispatchId);
  push('Emergency Event ID', m.eccEventId || m.incidentId);
  push('Ambulance Booking ID', m.ambulanceBookingId);
  push('Hospital Handover ID', m.hospitalHandoverId);

  push('Insurance Provider ID', m.insurerNhcId);
  push('TPA ID', m.tpaId);
  push('Claim ID', m.claimId);
  push('Policy ID', m.policyNumber || m.policyId);
  push('Pre-auth ID', m.preAuthId);
  push('Claim Pack ID', m.claimPackId);

  push('Blood Bank ID', m.bloodBankNhcId);
  push('Donation ID', m.donationId);
  push('Blood Unit ID', m.bloodUnitId);

  push('Vaccination Center ID', m.centerNhcId);

  ids.push({ label: 'Generated by', value: 'NeoHealthCard' });

  return ids;
}

function renderTemplate(certType: string, data: any) {
  switch (certType) {
    case 'VACCINATION_CERTIFICATE':
    case 'VACCINATION_COMPLETION_CERTIFICATE':
      return <VaccinationCertificateTemplate data={data} />;
    case 'PRESCRIPTION_CERTIFICATE':
      return <PrescriptionTemplate data={data} />;
    case 'DISCHARGE_SUMMARY_CERTIFICATE':
      return <DischargeSummaryTemplate data={data} />;
    case 'AMBULANCE_TRANSPORT_CERTIFICATE':
    case 'AMBULANCE_TRANSPORT_CERTIFICATE_LEGACY':
      return <AmbulanceCertificateTemplate data={data} />;
    case 'EMERGENCY_EVENT_CERTIFICATE':
      return <ECCEmergencyCertificateTemplate data={data} />;
    case 'BLOOD_DONATION_CERTIFICATE':
      return <BloodDonationCertificateTemplate data={data} />;
    case 'INSURANCE_CLAIM_CERTIFICATE':
      return <InsuranceClaimPackTemplate data={data} />;
    case 'LAB_REPORT':
    case 'LAB_REPORT_PDF':
      return <LabReportTemplate data={data} />;
    case 'INVOICE':
    case 'VACCINATION_RECEIPT':
      return <InvoiceReceiptTemplate data={data} />;
    default:
      break;
  }

  const mapped = TEMPLATE_MAP[certType];
  switch (mapped) {
    case 'AppointmentReceiptTemplate':
      return <AppointmentReceiptTemplate data={data} />;
    case 'PharmacyDocumentTemplate':
      return <PharmacyDocumentTemplate data={data} />;
    case 'HospitalDocumentTemplate':
      return <HospitalDocumentTemplate data={data} />;
    case 'AmbulanceInvoiceTemplate':
      return <AmbulanceInvoiceTemplate data={data} />;
    case 'BloodBankDocumentTemplate':
      return <BloodBankDocumentTemplate data={data} />;
    case 'InsuranceDocumentTemplate':
      return <InsuranceDocumentTemplate data={data} />;
    case 'LabDocumentTemplate':
      return <LabDocumentTemplate data={data} />;
    case 'PaymentReceiptTemplate':
      return <PaymentReceiptTemplate data={data} />;
    default:
      return <CertificateTemplate data={data} />;
  }
}

export default function CertificatePreview() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const navigate = useNavigate();
  const [cert, setCert] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!certificateId) return;
    let alive = true;
    setLoading(true);
    patientApi.getCertificatePreview(certificateId)
      .then(c => { if (alive) setCert(c); })
      .catch(() => { if (alive) setError('Certificate not found or access denied'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [certificateId]);

  if (loading) return <div className="flex justify-center py-20"><Spinner size="lg" /></div>;
  if (error || !cert) return (
    <div className="max-w-2xl mx-auto text-center py-20">
      <p className="text-red-500 mb-4">{error || 'Not found'}</p>
      <button onClick={() => navigate(-1)} className="text-teal-600 hover:underline text-sm">Go back</button>
    </div>
  );

  const metadata = cert.previewData || cert.metadata || {};
  const templateData = buildTemplateData(cert.certificateType, metadata);
  const linkedIds = buildLinkedIds(cert.certificateType, cert, metadata);

  const certType = cert.certificateType;
  const mapped = TEMPLATE_MAP[certType];

  // Compliance and provider identifiers come exclusively from the issuer's
  // recorded metadata — the layout hides any field that is absent.
  const compliance = {
    gstin: metadata.gstin,
    pan: metadata.pan,
    stateCode: metadata.stateCode,
    cin: metadata.cin,
  };

  const providerIds: any = {};
  if (certType.includes('PRESCRIPTION') || certType.includes('FITNESS') || certType.includes('SICK_LEAVE') || certType.includes('CHRONIC_CARE') || certType.includes('CHECKUP')) {
    providerIds.nhcProviderId = metadata.issuerNhcId;
    providerIds.registrationNo = metadata.doctorLicense;
    providerIds.nmc = metadata.nmc;
    providerIds.qualification = metadata.qualification;
    providerIds.specialty = metadata.speciality || metadata.specialty;
  } else if (certType.includes('LAB') || mapped === 'LabDocumentTemplate') {
    providerIds.nhcProviderId = metadata.labNhcId;
    providerIds.registrationNo = metadata.labRegNo;
    providerIds.nablCertNo = metadata.nablCertNo;
    providerIds.isoCertNo = metadata.isoCertNo;
    providerIds.pathologistId = metadata.pathologistId;
  } else if (certType.includes('AMBULANCE') || mapped === 'AmbulanceInvoiceTemplate') {
    providerIds.nhcProviderId = metadata.ambulanceProviderId;
    providerIds.vehicleRegNo = metadata.ambulanceNo;
    providerIds.fitnessCertNo = metadata.fitnessCertNo;
    providerIds.paramedicId = metadata.paramedicId;
  } else if (certType.includes('INSURANCE') || mapped === 'InsuranceDocumentTemplate') {
    providerIds.nhcProviderId = metadata.insurerNhcId;
    providerIds.irdaiRegNo = metadata.irdaiRegNo;
    providerIds.tpaId = metadata.tpaId;
  } else if (certType.includes('BLOOD') || mapped === 'BloodBankDocumentTemplate') {
    providerIds.nhcProviderId = metadata.bloodBankNhcId;
    providerIds.bloodBankLicenseNo = metadata.bloodBankLicense;
    providerIds.fdaLicenseNo = metadata.fdaLicenseNo;
  } else if (mapped === 'PharmacyDocumentTemplate') {
    providerIds.nhcProviderId = metadata.pharmacyNhcId;
    providerIds.drugLicenseNo = metadata.drugLicenseNo;
    providerIds.pharmacistId = metadata.pharmacistId;
  } else {
    providerIds.nhcProviderId = metadata.issuerNhcId;
    providerIds.registrationNo = metadata.hospitalRegNo;
    providerIds.accreditations = metadata.accreditations;
  }

  const header = {
    institutionName: metadata.institutionName || cert.issuerName || 'Issuer not recorded',
    institutionType: metadata.institutionType || cert.issuerType,
    institutionAddress: metadata.institutionAddress,
    institutionPhone: metadata.institutionPhone,
    institutionEmail: metadata.institutionEmail,
    accreditation: metadata.accreditation,
    registrationNo: metadata.registrationNo,
    compliance,
    providerIds,
  };

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="no-print flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <OfficialDocumentLayout
        documentTitle={cert.title}
        /**
         * The ISSUER's number. This was `cert.certificateId` — the Mongo id —
         * so a certificate handed to an employer carried a database key where
         * its number belongs. Absent when the issuer minted none, and the
         * layout then hides the field rather than inventing one.
         */
        documentNumber={cert.certificateNumber || undefined}
        /* Named by the server, so this page no longer keeps a label map that
           drifted from the enum. */
        documentType={cert.typeLabel || cert.certificateType.replace(/_/g, ' ')}
        issueDate={cert.issueDate}
        expiryDate={cert.expiryDate}
        status={cert.status}
        header={header}
        /* Who it is about — from the server, which reads the caller's own
           profile. All four of these were empty strings or absent, so an
           official-looking document rendered with a blank name and card id. */
        patientName={cert.patientName || undefined}
        patientAge={cert.patientAge ?? undefined}
        patientGender={cert.patientGender || undefined}
        nhcId={cert.patientNhcId || undefined}
        neoVerifyId={cert.neoVerifyId}
        /* `verifyUrl`, `qrPayload` and `documentHash` were passed here and no
           endpoint on this platform sends any of them. The NeoVerify id above
           is real, and is what a provider checks. */
        verificationStatus={cert.verificationStatus}
        registeredAt={cert.createdAt}
        linkedIds={linkedIds}
        watermark={getWatermark(cert.status)}
        onPrint={() => window.print()}
        /* Was also window.print(), which prints the browser page rather than
           handing over a file. The document is the page, so printing is what
           this genuinely does — and it says so instead of promising a
           download it cannot produce. */
        onShare={() => navigate(`/patient/share-center`)}
        /* The server's statuses are ACTIVE / EXPIRED / REVOKED. This compared
           against ISSUED and VERIFIED, so the control never appeared. */
        showRevoke={cert.status === 'ACTIVE'}
      >
        {renderTemplate(cert.certificateType, templateData)}
      </OfficialDocumentLayout>
    </div>
  );
}
